/**
 * PersistenceAdapter — wraps localStorage / IndexedDB per
 * /design/specs/4_architecture_c4_l3_components.md §2.9.
 *
 * V1: localStorage primary (no IndexedDB usage yet — payloads stay tiny).
 *
 * All state operations are typed and serialized through this adapter so
 * SessionManager and UI components never touch localStorage directly.
 */

const KEYS = {
  CONSENT_FLAG: 'pokersharp_consent_v1',
  ACTIVE_SESSION: 'pokersharp_active_session_v1',
  LAST_CONFIG: 'pokersharp_last_config_v1',
  CLIENT_UUID: 'pokersharp_client_uuid',
} as const;

import type { Session, SessionConfig } from '@/types/session';

export interface PersistenceAdapter {
  // Consent flag
  getConsentFlag(): boolean;
  setConsentFlag(v: boolean): void;

  // Active session (the one currently in progress)
  saveActiveSession(session: Session): void;
  loadActiveSession(): Session | null;
  clearActiveSession(): void;

  // Last-used config (persisted across sessions so the form prefills)
  saveLastConfig(config: SessionConfig): void;
  loadLastConfig(): SessionConfig | null;
}

class LocalStoragePersistenceAdapter implements PersistenceAdapter {
  getConsentFlag(): boolean {
    return localStorage.getItem(KEYS.CONSENT_FLAG) === 'true';
  }

  setConsentFlag(v: boolean): void {
    if (v) localStorage.setItem(KEYS.CONSENT_FLAG, 'true');
    else localStorage.removeItem(KEYS.CONSENT_FLAG);
  }

  saveActiveSession(session: Session): void {
    localStorage.setItem(KEYS.ACTIVE_SESSION, JSON.stringify(session));
  }

  loadActiveSession(): Session | null {
    const raw = localStorage.getItem(KEYS.ACTIVE_SESSION);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as Session;
    } catch {
      // Corrupt payload — wipe and recover.
      localStorage.removeItem(KEYS.ACTIVE_SESSION);
      return null;
    }
  }

  clearActiveSession(): void {
    localStorage.removeItem(KEYS.ACTIVE_SESSION);
  }

  saveLastConfig(config: SessionConfig): void {
    localStorage.setItem(KEYS.LAST_CONFIG, JSON.stringify(config));
  }

  loadLastConfig(): SessionConfig | null {
    const raw = localStorage.getItem(KEYS.LAST_CONFIG);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as SessionConfig;
    } catch {
      localStorage.removeItem(KEYS.LAST_CONFIG);
      return null;
    }
  }
}

/** Single shared instance used across the app. */
export const persistence: PersistenceAdapter = new LocalStoragePersistenceAdapter();

/** Exported for tests / migration tooling. */
export const PERSISTENCE_KEYS = KEYS;
